import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Send, ClipboardList, Bell, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../components/Toast';
import DepartmentSelect from '../components/DepartmentSelect';
import { STANDARD_DEPARTMENTS } from '../data/departments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FRONTEND = process.env.REACT_APP_BACKEND_URL?.replace('/api', '') || window.location.origin;

export default function Surveys() {
  const { show, node } = useToast();
  const location = useLocation();
  const [reviews, setReviews] = useState([]);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [companyDepartments, setCompanyDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [targetDepartment, setTargetDepartment] = useState(
    location.state?.preferredDepartment || STANDARD_DEPARTMENTS[0]
  );

  const fetchData = useCallback(async () => {
    try {
      const [rev, emp, settings] = await Promise.all([
        axios.get(`${API}/reviews`, { withCredentials: true }),
        axios.get(`${API}/employees`, { withCredentials: true }),
        axios.get(`${API}/settings/departments`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      setReviews(rev.data.reviews || []);
      setEmployeeCount(emp.data.total || 0);
      setCompanyDepartments(settings.data.departments || []);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (location.state?.preferredDepartment) {
      setTargetDepartment(location.state.preferredDepartment);
      setConfirm(true);
    }
  }, [location.state]);

  const handleLaunch = async () => {
    if (!targetDepartment?.trim()) {
      show('Please select a target department.', 'error');
      return;
    }
    setLaunching(true);
    try {
      const { data } = await axios.post(
        `${API}/reviews/launch`,
        {
          title: 'Resilience Review',
          department: targetDepartment.trim(),
        },
        { withCredentials: true }
      );
      show(`Invites sent! Review launched for ${data.employee_count} employees.`);
      setConfirm(false);
      fetchData();
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to launch review', 'error');
    }
    setLaunching(false);
  };

  const handleReminders = async (reviewId) => {
    try {
      const { data } = await axios.post(
        `${API}/reviews/${reviewId}/send-reminders`,
        {},
        { withCredentials: true }
      );
      show(data.message);
      fetchData();
    } catch (err) {
      show(err.response?.data?.detail || 'Failed to send reminders', 'error');
    }
  };

  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl mx-auto" data-testid="surveys-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-slate-800">Surveys</h1>
          <p className="text-sm text-slate-500 mt-1">Launch and track Resilience Reviews</p>
        </div>
        <Button
          onClick={() => setConfirm(true)}
          disabled={employeeCount === 0}
          className="bg-teal-600 hover:bg-teal-700 text-white"
          data-testid="launch-review-btn"
        >
          <Send className="w-4 h-4 mr-2" /> Launch Resilience Review
        </Button>
      </div>

      {employeeCount === 0 && !loading && (
        <div className="glass-card rounded-2xl p-4 mb-6 text-sm text-amber-700 bg-amber-50/60 border border-amber-100">
          Add employees under <strong>Teams</strong> before launching a review.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="glass-card rounded-2xl p-12 text-center">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No reviews launched yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r, i) => {
            const surveyUrl = `${FRONTEND}/assessment/${r.review_id}`;
            const hasPending = r.completed_count < r.employee_count;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card rounded-2xl p-5"
              >
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-medium text-slate-800">{r.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {r.completed_count}/{r.employee_count} completed · {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'active' && hasPending && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-amber-600 border-amber-200 hover:bg-amber-50 text-xs"
                        onClick={() => handleReminders(r.review_id)}
                        data-testid={`send-reminder-${i}`}
                      >
                        <Bell className="w-3.5 h-3.5 mr-1" /> Send Reminders
                      </Button>
                    )}
                    <span
                      className={`text-xs font-medium px-3 py-1 rounded-full ${
                        r.status === 'active' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-slate-50">
                  <span className="text-xs text-slate-400 font-mono truncate flex-1">{surveyUrl}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-teal-600 text-xs shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(surveyUrl);
                      show('Link copied!');
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 w-full max-w-md mx-4 shadow-2xl text-left"
          >
            <div className="w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center mx-auto mb-4">
              <Send className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-heading text-xl font-medium text-slate-800 mb-2 text-center">
              Launch Resilience Review?
            </h3>
            <p className="text-sm text-slate-500 mb-5 text-center">
              Invites go to all <strong>{employeeCount}</strong> employees. Choose the focus department for this pulse.
            </p>
            <DepartmentSelect
              id="survey-launch-department"
              label="Target department"
              value={targetDepartment}
              onChange={setTargetDepartment}
              companyDepartments={companyDepartments}
              required
              className="mb-6"
              data-testid="survey-launch-department"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setConfirm(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleLaunch}
                disabled={launching || !targetDepartment?.trim()}
                className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="confirm-launch-btn"
              >
                {launching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invites'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
      {node}
    </div>
  );
}
